import { SelectionManager } from 'utils/selectionManager';
import { fetchContacts } from 'api/customers';

const { captureException } = require('utils/errors');
const { SessionJsonStorage } = require('utils/storage');

const QUERY_KEY = 'contacts-list';

// Create a selection manager instance for contacts
const contactSelectionManager = new SelectionManager('contacts-selection');

export class ContactList {
  static async fetch(queryParams) {
    return fetchContacts(queryParams)
      .then(({ results: data = [], count: total }) => {
        const { page, page_size, ordering } = queryParams;

        if (ContactList.page_size !== page_size || ContactList.ordering !== ordering) {
          ContactList.update({
            pages: {
              [page]: data.map(({ id }) => id),
            },
          });
        } else {
          ContactList.update({
            pages: {
              ...ContactList.get().pages,
              [page]: data.map(({ id }) => id),
            },
          });
        }
        ContactList.update({
          page_size,
          ordering,
          count: total,
        });
        return {
          page,
          data,
          total,
        };
      })
      .catch(captureException);
  }

  static get ordering() {
    return SessionJsonStorage.get(QUERY_KEY)?.ordering || '-created_at';
  }

  static get page_size() {
    return SessionJsonStorage.get(QUERY_KEY)?.page_size || 20;
  }

  static get count() {
    return SessionJsonStorage.get(QUERY_KEY)?.count || 0;
  }

  static get hasNext() {
    const { pages } = ContactList.get();

    // If no pages cached, assume there might be more
    if (!pages || Object.keys(pages).length === 0) {
      return true;
    }

    // Get the highest page number that was fetched
    const pageNumbers = Object.keys(pages).map(Number);
    const lastPageNumber = Math.max(...pageNumbers);
    const lastPageData = pages[lastPageNumber];

    // If the last fetched page is empty, there are no more pages
    if (lastPageData && lastPageData.length === 0) {
      return false;
    }

    // If the last page has fewer items than page_size, it's likely the last page
    if (lastPageData && lastPageData.length < ContactList.page_size) {
      return false;
    }

    // Otherwise, assume there might be more pages
    return true;
  }

  static update(payload) {
    SessionJsonStorage.set({
      key: QUERY_KEY,
      value: {
        ...ContactList.get(),
        ...payload,
      },
    });
  }

  static get() {
    return SessionJsonStorage.get(QUERY_KEY) || {
      ordering: '-created_at',
      page_size: 20,
      pages: {},
    };
  }

  // Selection management methods using generic SelectionManager
  static getSelection() {
    return contactSelectionManager.getSelection();
  }

  static clearSelection() {
    contactSelectionManager.clearSelection();
  }

  static setSelectAll(selectAll) {
    contactSelectionManager.setSelectAll(selectAll);
  }

  static isSelected(contactId) {
    return contactSelectionManager.isSelected(contactId);
  }

  static toggleSelection(contactId) {
    contactSelectionManager.toggleSelection(contactId);
  }

  static getSelectedIds() {
    return contactSelectionManager.getSelectedIds();
  }

  static getSelectedCount() {
    return contactSelectionManager.getSelectedCount(ContactList.count);
  }

  static getPageSelectedIds(pageContacts) {
    return contactSelectionManager.getPageSelectedIds(pageContacts);
  }

  static isPageFullySelected(pageContacts) {
    return contactSelectionManager.isPageFullySelected(pageContacts);
  }

  static isPagePartiallySelected(pageContacts) {
    return contactSelectionManager.isPagePartiallySelected(pageContacts);
  }

  static async getNext(id) {
    const { pages } = ContactList.get();
    const list = Object.entries(pages).find(([page, ids]) => ids.includes(+id));
    if (list) {
      const [page, ids] = list;
      const index = ids.indexOf(+id);
      const nextId = ids[index + 1];
      if (nextId) {
        return nextId;
      }
      const nextPage = +page + 1;
      if (pages[nextPage]?.length) {
        return pages[nextPage][0];
      }
    }
    if (!ContactList.hasNext) {
      return undefined;
    }
    // Find the next page number correctly
    const pageNumbers = Object.keys(pages).map(Number);
    const nextPage = pageNumbers.length > 0 ? Math.max(...pageNumbers) + 1 : 1;
    const nextIds = (await ContactList.fetch({
      ordering: ContactList.ordering,
      page: nextPage,
      page_size: ContactList.page_size,
    }).then((res) => (res && res.data ? res.data.map((c) => c.id) : [])));
    SessionJsonStorage.set({
      key: QUERY_KEY,
      value: {
        ...ContactList.get(),
        pages: {
          ...pages,
          [nextPage]: nextIds,
        },
      },
    });
    if (!nextIds || nextIds.length === 0) {
      return undefined;
    }
    return nextIds[0];
  }

  static async getPrevId(id) {
    const { pages } = ContactList.get();
    const idNum = +id;
    // Sort pages numerically
    const entries = Object.entries(pages)
      .map(([page, ids]) => [Number(page), ids])
      .sort((a, b) => a[0] - b[0]);
    const listIndex = entries.findIndex(([_, ids]) => ids.includes(idNum));
    if (listIndex !== -1) {
      const [page, ids] = entries[listIndex];
      const index = ids.indexOf(idNum);
      if (index > 0) {
        const prevId = ids[index - 1];
        return prevId;
      }
      if (listIndex > 0) {
        // Get last id from previous cached page
        const [prevPage, prevIds] = entries[listIndex - 1];
        const prevId = prevIds[prevIds.length - 1];
        return prevId;
      }
      // This is the first page in cache, check if there is a previous page (not cached)
      const minPage = entries.length > 0 ? entries[0][0] : 1;
      const prevPage = minPage - 1;
      if (prevPage >= 1) {
        const prevIds = (await ContactList.fetch({
          ordering: ContactList.ordering,
          page: prevPage,
          page_size: ContactList.page_size,
        }).then((res) => (res && res.data ? res.data.map((c) => c.id) : [])));
        SessionJsonStorage.set({
          key: QUERY_KEY,
          value: {
            ...ContactList.get(),
            pages: {
              ...pages,
              [prevPage]: prevIds,
            },
          },
        });
        if (prevIds.length > 0) {
          const prevId = prevIds[prevIds.length - 1];
          return prevId;
        }
      }
    }
    return undefined;
  }
}
